// src/components/InventoryTransactionsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import useDataFetch from '../hooks/useDataFetch';
import { useCategories } from '../hooks/useCategories';
import { getFields, searchFields } from '../utils/fields';
import { formatDateTime } from '../utils/format';
import SearchForm from '../components/SearchForm';
import TableView from '../components/TableView';
import Pagination from '../components/Pagination';
import RelatedMenu from '../components/RelatedMenu';
import { Link, Typography, Paper, Box, Stack, Chip } from "@mui/material";

const InventoryTransactionsPage = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState([]);
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await axiosInstance.get('/warehouse', { params: { activeOnly: true } });
        setWarehouses(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(err);
        }
      }
    };
    fetchWarehouses();
  }, []);

  const {
    dataset,
    error,
    currentPage,
    totalPages,
    searchQuery,
    setSearchQuery,
    handleSearchChange,
    handleSearchSubmit,
    handleClearSearch,
    handlePageChange,
  } = useDataFetch('/inventory/transactions', { keywords: '', category_id: '', date: '' });
  const categories = useCategories();

  // 行クリック時にページ遷移を行う処理
  const handleRowClick = (item) => {
    navigate(`/products/${item.product_id}`);
  };
  const transactionTypeColorMap = {
    '入庫': 'green',
    '出庫': 'red',
    '返品': 'orange',
    '調整': 'blue',
    '予約': 'purple',
    '予約解除': 'gray',
  };
  // 検索フィールド定義
  const fieldsToInclude = ['keywords', 'category_id', 'dates'];
  let searchItems = getFields(searchFields, fieldsToInclude);

  // カラム定義
  const columns = [
    { label: '日付', accessor: 'transaction_date', format: (date) => formatDateTime(date), width: '160px', },
    {
      label: '品番',
      accessor: 'sku',
      render: (item) => (
        <Link
          onClick={() => handleRowClick(item)}>
          {item.sku}
        </Link>
      ),
      width: '180px',
    },
    {
      label: '商品名',
      accessor: 'product_name',
      width: '350px',
      render: (item) => (
        <Box>
          <Typography>{item.product_name}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {item?.option_combination &&
              Object.entries(item.option_combination).map(([type, value]) => (
                <React.Fragment key={type}>
                  {" "}
                  {type}: {value}
                </React.Fragment>
              ))}
          </Typography>
        </Box>
      )
    },
    { label: '倉庫', accessor: 'warehouse_name', minWidth: '100px' },
    {
      label: '入出庫数',
      accessor: 'quantity',
      render: (item) => {
        const color = transactionTypeColorMap[item.transaction_type];
        return (
          <span style={{ color }}>
            {item.quantity} {item.transaction_type}
          </span>
        );
      },
      width: '160px',
    },
    { label: '在庫数', accessor: 'stock_remaining', width: '120px' },
    { label: 'ユーザ', accessor: 'username', width: '120px' },
  ];
  const links = [
    { path: '/products', label: '在庫一覧', icon: 'inventory_2' },
    { path: '/inventory/transactions/register', label: '入出庫登録', icon: 'sync_alt' },
    { path: '/products/new', label: '新規商品登録', icon: 'post_add' },
  ];
  useEffect(() => {
    handleSearchSubmit(); // 最新のwarehouse_idsで検索
  }, [searchQuery.warehouse_ids]);

  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Typography variant="h1" component="h1">入出庫履歴</Typography>
      <div className='grid2-2_1'>

        {/* 検索フォーム */}
        <SearchForm
          searchQuery={searchQuery}
          searchFields={searchItems}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleClearSearch={handleClearSearch}
          categories={categories}
        />
        <RelatedMenu links={links} />
      </div>
      <Paper variant='section'>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems='center' justifyContent='end' sx={{ mb: 2 }}>
          <Typography variant="caption">倉庫で絞り込み</Typography>
          {warehouses.map((w) => (
            <Chip
              key={w.warehouse_id}
              label={w.warehouse_name}
              size='medium'
              clickable
              color={warehouseFilter.includes(w.warehouse_id) ? 'primary' : 'default'}
              onClick={() => {
                const warehouseId = w.warehouse_id;
                const newFilter = warehouseFilter.includes(warehouseId)
                  ? warehouseFilter.filter(id => id !== warehouseId)
                  : [...warehouseFilter, warehouseId];

                setWarehouseFilter(newFilter);
                setSearchQuery(prev => ({
                  ...prev,
                  warehouse_ids: newFilter,
                }));
              }}
              sx={{ mb: 1 }}
            />
          ))}
        </Stack>
        {/* テーブル */}
        <TableView dataset={dataset} columns={columns} />

        {/* ページネーション */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Paper>
    </>
  );
};

export default InventoryTransactionsPage;
